import axios, { CancelTokenSource } from 'axios';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { getStatusCode } from './errorClassification';
import { handleApiError, MutationMeta } from './errorPipeline';
import { showSuccessToast } from 'src/utils';

// Registers MutationMeta as the shape of every query/mutation `meta` object,
// so `meta: { successMessage: '...' }` etc. is typed everywhere it's set and
// read (errorPipeline.ts, emitSuccessToast below).
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: MutationMeta;
    mutationMeta: MutationMeta;
  }
}

/** 4xx failures won't fix themselves on retry; 5xx/network get up to 3 attempts. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = getStatusCode(error);
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

function emitSuccessToast(data: unknown, meta: MutationMeta | undefined) {
  if (!meta || meta.silent) return;
  const message = meta.successMessageFn
    ? meta.successMessageFn(data)
    : meta.successMessage;
  if (message) showSuccessToast(message);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => handleApiError(err, query.meta, 'query'),
  }),
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) =>
      handleApiError(err, mutation.meta, 'mutation'),
    onSuccess: (data, _variables, _context, mutation) =>
      emitSuccessToast(data, mutation.meta),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      structuralSharing: true,
    },
    mutations: { retry: false },
  },
});

/**
 * NSwag's axios clients take the legacy axios `CancelToken`, not an
 * `AbortSignal`. This bridges v5's `queryFn({ signal })` to that legacy
 * token so `queryFn: ({ signal }) => xApiClient.get(toCancelToken(signal))`
 * actually cancels the in-flight request.
 */
/**
 * A tick that settles as an error has already exhausted the retry policy
 * above, so a `refetchInterval` callback stops there: the state that would
 * otherwise end the poll can no longer arrive, and the alternative is
 * hammering — and toasting about — a down API on every tick. A window focus or
 * an invalidation restarts it. Note that `fetchFailureCount` cannot be used
 * for this: every fetch resets it, so it never accumulates across ticks.
 */
export const hasPollTickFailed = (state: { status: string }) =>
  state.status === 'error';

export function toCancelToken(signal: AbortSignal | undefined) {
  const source: CancelTokenSource = axios.CancelToken.source();
  if (!signal) return source.token;

  if (signal.aborted) {
    source.cancel('Aborted');
    return source.token;
  }

  signal.addEventListener('abort', () => source.cancel('Aborted'), {
    once: true,
  });
  return source.token;
}
